(() => {
  if (window.NailelfSizeCart) return;

  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'CUSTOM SIZE'];
  const productCache = new Map();
  let modal;
  let activeState = null;

  const selectors = {
    form: '[data-nailelf-size-form]',
    handle: '[data-nailelf-product-handle]'
  };

  const strings = {
    title: 'Choose your size',
    subtitle: 'Select a size before adding this product to cart.',
    customTitle: 'Custom size measurements',
    customHelp: 'Measure in millimeters from thumb to pinky. These details will be saved on the order.',
    leftHand: 'Left hand',
    rightHand: 'Right hand',
    leftPlaceholder: 'Thumb to pinky, e.g. 15, 12, 13, 12, 10 mm',
    rightPlaceholder: 'Thumb to pinky, e.g. 15, 12, 13, 12, 10 mm',
    notes: 'Size notes',
    notesPlaceholder: 'Optional: preferred fit or measurement notes',
    add: 'Add to cart',
    adding: 'Adding...',
    close: 'Close',
    unavailable: 'This size is unavailable.',
    chooseSize: 'Please choose a size.',
    fillMeasurements: 'Please enter both left and right hand measurements.',
    error: 'Could not add this product. Please try again.'
  };

  const normalise = (value) => String(value || '').trim();
  const normaliseKey = (value) => normalise(value).replace(/\s+/g, ' ').toUpperCase();
  const isDefaultTitle = (value) => /^(default title|title)$/i.test(normalise(value));
  const isCustomSize = (value) => /custom/i.test(normalise(value));
  const isSizeName = (value) => /size/i.test(normalise(value));

  function getRoutesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function productUrl(handle) {
    const root = getRoutesRoot().replace(/\/?$/, '/');
    return `${root}products/${encodeURIComponent(handle)}.js`;
  }

  async function loadProduct(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);
    const request = fetch(productUrl(handle), { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);
        return response.json();
      });
    productCache.set(handle, request);
    return request;
  }

  function handleFromForm(form) {
    if (form.dataset.nailelfProductHandle) return form.dataset.nailelfProductHandle;
    const holder = form.closest(selectors.handle);
    if (holder && holder.dataset.nailelfProductHandle) return holder.dataset.nailelfProductHandle;
    const scope = form.closest('li, article, .card-wrapper, .product, section') || document;
    const link = scope.querySelector('a[href*="/products/"]');
    if (!link) return '';
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      const parts = url.pathname.split('/products/');
      if (!parts[1]) return '';
      return decodeURIComponent(parts[1].split('/')[0]).replace(/\.js$/, '');
    } catch (error) {
      return '';
    }
  }

  function variantOption(variant, index) {
    if (Array.isArray(variant.options)) return variant.options[index];
    return variant[`option${index + 1}`];
  }

  function sizeOptionIndex(product) {
    const options = Array.isArray(product.options) ? product.options : [];
    const named = options.findIndex((option) => isSizeName(option && (option.name || option)));
    if (named >= 0) return named;
    const firstOptions = (product.variants || []).map((variant) => variantOption(variant, 0)).filter(Boolean);
    if (firstOptions.some((value) => !isDefaultTitle(value) && (/^(XS|S|M|L)$/i.test(normalise(value)) || isCustomSize(value)))) return 0;
    if ((product.variants || []).some((variant) => /(^|\/|\s)(XS|S|M|L)(\s|\/|$)|custom size/i.test(variant.title || ''))) return 0;
    return -1;
  }

  function parseSizeValue(value) {
    const label = normalise(value);
    if (!label) return { label: '', measurement: '' };
    if (isCustomSize(label)) return { label: 'Custom Size', measurement: '' };
    const normalized = label.replace(/\uFF1A/g, ':');
    const parts = normalized.split(':');
    const name = normalise(parts.shift());
    const upper = name.toUpperCase();
    if (['XS', 'S', 'M', 'L'].includes(upper)) {
      return { label: upper, measurement: normalise(parts.join(':')) };
    }
    return { label, measurement: '' };
  }

  function displayLabel(value) {
    return parseSizeValue(value).label;
  }

  function orderScore(label) {
    const key = normaliseKey(label);
    const index = SIZE_ORDER.indexOf(key);
    return index >= 0 ? index : SIZE_ORDER.length + key.charCodeAt(0);
  }

  function getSizeChoices(product) {
    const variants = Array.isArray(product && product.variants) ? product.variants : [];
    if (variants.length <= 1) return [];

    const index = sizeOptionIndex(product);
    if (index < 0) return [];

    const choices = new Map();
    variants.forEach((variant) => {
      const raw = variantOption(variant, index) || variant.title;
      if (!raw || isDefaultTitle(raw)) return;
      const parsed = parseSizeValue(raw);
      const label = parsed.label;
      const key = normaliseKey(label);
      const current = choices.get(key);
      if (!current || (!current.variant.available && variant.available)) {
        choices.set(key, {
          key,
          label,
          measurement: parsed.measurement,
          isCustom: isCustomSize(label),
          variant
        });
      }
    });

    const result = Array.from(choices.values()).sort((a, b) => orderScore(a.label) - orderScore(b.label));
    const hasStandardSizes = result.some((choice) => ['XS', 'S', 'M', 'L'].includes(choice.key));
    const hasCustomSize = result.some((choice) => choice.isCustom);
    if (hasStandardSizes && !hasCustomSize) {
      const medium = variants.find((variant) => variant.available && normaliseKey(displayLabel(variantOption(variant, index) || variant.title)) === 'M');
      const fallback = medium || variants.find((variant) => variant.available) || variants[0];
      if (fallback) {
        result.push({
          key: 'CUSTOM SIZE',
          label: 'Custom Size',
          measurement: '',
          isCustom: true,
          variant: fallback
        });
      }
    }
    return result;
  }

  function createModal() {
    const node = document.createElement('div');
    node.className = 'nsc-modal';
    node.setAttribute('aria-hidden', 'true');
    node.innerHTML = `
      <div class="nsc-modal__backdrop" data-nsc-close></div>
      <div class="nsc-modal__panel" role="dialog" aria-modal="true" aria-labelledby="nsc-title" tabindex="-1">
        <button class="nsc-modal__close" type="button" data-nsc-close aria-label="${strings.close}">&times;</button>
        <div class="nsc-modal__head">
          <p class="nsc-modal__eyebrow">NAILELF</p>
          <h2 id="nsc-title">${strings.title}</h2>
          <p data-nsc-subtitle>${strings.subtitle}</p>
        </div>
        <div class="nsc-modal__sizes" data-nsc-sizes></div>
        <div class="nsc-modal__custom" data-nsc-custom hidden>
          <div>
            <strong>${strings.customTitle}</strong>
            <span>${strings.customHelp}</span>
          </div>
          <label>${strings.leftHand}<input type="text" data-nsc-left placeholder="${strings.leftPlaceholder}"></label>
          <label>${strings.rightHand}<input type="text" data-nsc-right placeholder="${strings.rightPlaceholder}"></label>
          <label>${strings.notes}<textarea rows="3" data-nsc-notes placeholder="${strings.notesPlaceholder}"></textarea></label>
        </div>
        <p class="nsc-modal__error" data-nsc-error hidden></p>
        <button class="nsc-modal__add" type="button" data-nsc-add disabled>${strings.add}</button>
      </div>
    `;
    document.body.appendChild(node);

    node.addEventListener('click', (event) => {
      if (event.target.closest('[data-nsc-close]')) closeModal();
      const sizeButton = event.target.closest('[data-nsc-size]');
      if (sizeButton) selectChoice(sizeButton.dataset.nscSize);
      if (event.target.closest('[data-nsc-add]')) confirmChoice();
    });
    node.addEventListener('input', validateModal);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && node.classList.contains('is-open')) closeModal();
    });

    return node;
  }

  function getModal() {
    if (!modal) modal = createModal();
    return modal;
  }

  function setError(message) {
    const node = getModal().querySelector('[data-nsc-error]');
    node.textContent = message || '';
    node.hidden = !message;
  }

  function openModal(state) {
    activeState = state;
    const node = getModal();
    const sizeRoot = node.querySelector('[data-nsc-sizes]');
    const subtitle = node.querySelector('[data-nsc-subtitle]');
    subtitle.textContent = state.product.title || strings.subtitle;
    sizeRoot.innerHTML = '';
    setError('');

    state.choices.forEach((choice) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nsc-size';
      button.dataset.nscSize = choice.key;
      button.disabled = !choice.variant.available;
      button.innerHTML = `<strong>${choice.label}</strong>${choice.measurement ? `<small>${choice.measurement}</small>` : ''}${choice.variant.available ? '' : `<span>${strings.unavailable}</span>`}`;
      sizeRoot.appendChild(button);
    });

    node.classList.add('is-open');
    node.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('nsc-lock');
    document.body.classList.add('nsc-lock');
    node.querySelector('.nsc-modal__panel').focus({ preventScroll: true });
    selectChoice('');
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('nsc-lock');
    document.body.classList.remove('nsc-lock');
    activeState = null;
  }

  function selectChoice(key) {
    if (!activeState) return;
    activeState.choice = activeState.choices.find((choice) => choice.key === key) || null;
    const node = getModal();
    node.querySelectorAll('[data-nsc-size]').forEach((button) => {
      button.classList.toggle('is-selected', activeState.choice && button.dataset.nscSize === activeState.choice.key);
    });
    const customPanel = node.querySelector('[data-nsc-custom]');
    customPanel.hidden = !(activeState.choice && activeState.choice.isCustom);
    validateModal();
  }

  function validateModal() {
    if (!activeState) return;
    const node = getModal();
    const add = node.querySelector('[data-nsc-add]');
    const choice = activeState.choice;
    let disabled = !choice || !choice.variant.available;
    if (choice && choice.isCustom) {
      disabled = disabled || !node.querySelector('[data-nsc-left]').value.trim() || !node.querySelector('[data-nsc-right]').value.trim();
    }
    add.disabled = disabled;
  }

  function selectedProperties(choice) {
    const node = getModal();
    const properties = {
      'Selected size': choice.isCustom ? 'Custom size' : choice.label
    };
    if (choice.isCustom) {
      properties['Left hand measurements'] = node.querySelector('[data-nsc-left]').value.trim();
      properties['Right hand measurements'] = node.querySelector('[data-nsc-right]').value.trim();
      const notes = node.querySelector('[data-nsc-notes]').value.trim();
      if (notes) properties['Custom size notes'] = notes;
    }
    return properties;
  }

  /* NAILELF CUSTOM SIZE PROPERTY RESET 20260730 */
  function clearControlledSizeProperties(formData) {
    const controlledNames = new Set([
      'selected size',
      'size',
      'nail size',
      'left hand measurements',
      'right hand measurements',
      'custom size notes'
    ]);
    const keysToDelete = [];
    for (const key of formData.keys()) {
      const match = /^properties\[(.+)\]$/i.exec(key);
      if (!match) continue;
      const propertyName = normalise(match[1]).toLowerCase();
      if (controlledNames.has(propertyName)) keysToDelete.push(key);
    }
    keysToDelete.forEach((key) => formData.delete(key));
  }

  function appendProperties(formData, properties) {
    Object.entries(properties || {}).forEach(([key, value]) => {
      formData.set(`properties[${key}]`, value);
    });
  }

  function cartTarget() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  async function addVariant(state, choice, properties) {
    const cart = cartTarget();
    const submitter = state.submitter;
    const addButton = modal && modal.querySelector('[data-nsc-add]');
    const formData = new FormData(state.form);
    formData.set('id', choice.variant.id);
    if (!formData.get('quantity')) formData.set('quantity', '1');
    clearControlledSizeProperties(formData);
    appendProperties(formData, properties);

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }

    if (submitter) {
      submitter.setAttribute('aria-disabled', 'true');
      submitter.classList.add('loading');
    }
    if (addButton) {
      addButton.disabled = true;
      addButton.textContent = strings.adding;
    }

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    try {
      const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', config);
      const payload = await response.json();
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || strings.error);
      }
      closeModal();
      if (cart && typeof cart.renderContents === 'function') {
        cart.classList.remove('is-empty');
        cart.renderContents(payload);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
    } catch (error) {
      setError(error.message || strings.error);
    } finally {
      if (submitter) {
        submitter.classList.remove('loading');
        submitter.removeAttribute('aria-disabled');
      }
      if (addButton) {
        addButton.textContent = strings.add;
        validateModal();
      }
    }
  }

  function confirmChoice() {
    if (!activeState) return;
    const choice = activeState.choice;
    if (!choice) {
      setError(strings.chooseSize);
      return;
    }
    if (!choice.variant.available) {
      setError(strings.unavailable);
      return;
    }
    if (choice.isCustom) {
      const node = getModal();
      if (!node.querySelector('[data-nsc-left]').value.trim() || !node.querySelector('[data-nsc-right]').value.trim()) {
        setError(strings.fillMeasurements);
        return;
      }
    }
    addVariant(activeState, choice, selectedProperties(choice));
  }

  async function handleSubmit(event) {
    const form = event.target && event.target.closest && event.target.closest(selectors.form);
    if (!form) return;
    if (form.dataset.nailelfSizeBusy === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter || form.querySelector('[type="submit"], button[name="add"]');
    if (submitter && (submitter.disabled || submitter.getAttribute('aria-disabled') === 'true')) return;

    const handle = handleFromForm(form);
    form.dataset.nailelfSizeBusy = 'true';
    try {
      const product = await loadProduct(handle);
      const choices = getSizeChoices(product);
      if (!choices.length) {
        const id = form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value;
        const variant = (product && product.variants || []).find((item) => String(item.id) === String(id)) || { id, available: true };
        await addVariant({ form, product: product || {}, submitter }, { label: 'Default', variant }, {});
        return;
      }
      openModal({ form, product, choices, submitter });
    } catch (error) {
      setError(error.message || strings.error);
      const id = form.querySelector('[name="id"]') && form.querySelector('[name="id"]').value;
      if (id) {
        await addVariant({ form, product: {}, submitter }, { label: 'Default', variant: { id, available: true } }, {});
      }
    } finally {
      delete form.dataset.nailelfSizeBusy;
    }
  }

  async function openHandle(handle, submitter) {
    const product = await loadProduct(handle);
    const choices = getSizeChoices(product);
    const form = document.createElement('form');
    form.setAttribute('data-nailelf-size-form', 'true');
    form.dataset.nailelfProductHandle = handle;
    form.style.display = 'none';
    const id = document.createElement('input');
    id.type = 'hidden';
    id.name = 'id';
    id.value = product && product.variants && product.variants[0] ? product.variants[0].id : '';
    const qty = document.createElement('input');
    qty.type = 'hidden';
    qty.name = 'quantity';
    qty.value = '1';
    form.appendChild(id);
    form.appendChild(qty);
    document.body.appendChild(form);
    if (!choices.length) {
      const variant = (product && product.variants && product.variants[0]) || { id: id.value, available: true };
      await addVariant({ form, product: product || {}, submitter }, { label: 'Default', variant }, {});
      window.setTimeout(() => form.remove(), 1000);
      return;
    }
    openModal({ form, product, choices, submitter });
  }

  document.addEventListener('submit', handleSubmit, true);
  window.NailelfSizeCart = { loadProduct, getSizeChoices, openHandle }; /* Nailelf 2026-07-09 v3 openHandle */
})();